import { ProgressSpinner } from "primereact/progressspinner";
import React, { useCallback, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill's default styling
import Select from "react-select";
import Http from "../../../Common/Http";

// Sample options for Select components
const assignedByOptions = [
  { value: "user1", label: "User One" },
  { value: "user2", label: "User Two" },
  { value: "user3", label: "User Three" },
];

const statusOptions = [
  { value: "done", label: "Done" },
  { value: "inProgress", label: "In Progress" },
  { value: "pending", label: "Pending" },
];

const assignUsersOptions = [
  { value: "user1", label: "User One" },
  { value: "user2", label: "User Two" },
  { value: "user3", label: "User Three" },
  { value: "user4", label: "User Four" },
];

const taskTypeOptions = [
  { value: "task", label: "Task" },
  { value: "bug", label: "Bug" },
  { value: "re_task", label: "ReTask" },
  { value: "maintenance", label: "Maintenance" },
];

const linkedIssueTypes = [
  { value: "blockedBy", label: "Blocked By" },
  { value: "blocks", label: "Blocks" },
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }], // Header levels
    ["bold", "italic", "underline"], // Formatting styles
    [{ color: [] }, { background: [] }], // Text and background color
    [{ list: "ordered" }, { list: "bullet" }], // Lists
    ["link"], // Hyperlinks
    ["clean"], // Remove formatting
  ],
};

const quillFormats = ["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"];

const Create = ({ setFormSubmit }) => {
  const [count, setCount] = useState(1);
  const [inputs, setInputs] = useState({
    name: "",
    taskType: null,
    assignedBy: null,
    assignedUsers: [],
    points: 0,
    status: null,
    description: "",
    comment: "",
    startDate: "",
    endDate: "",
    actualStartDate: "",
    actualStartTime: "",
    actualEndDate: "",
    actualEndTime: "",
    estimatedTime: "",
    documents: [],
    subTasks: [],
    reference_links: [],
    server_credentials: [],
    important_documents: [],
  });

  console.log(inputs);

  const [visibleFields, setVisibleFields] = useState(["subTasks", "linkedIssues"]); // Pre-select sections
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({}); // For form validation errors

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };
  // Handler for Select (single) changes
  const handleQuillChange = (content) => {
    setInputs((prev) => ({ ...prev, description: content }));
  };

  const handleCommentChange = (content) => {
    setInputs((prev) => ({ ...prev, comment: content }));
  };

  // Handler for Select (multi) changes
  const handleMultiSelectChange = useCallback((selectedOptions) => {
    setInputs((prev) => ({ ...prev, assignedUsers: selectedOptions }));
  }, []);

  // Handler for file input changes
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    setInputs((prev) => ({ ...prev, documents: files }));
  }, []);

  const handleSubTaskChange = (e, index, field) => {
    const updatedSubTasks = [...inputs.subTasks];
    updatedSubTasks[index][field] = e.target.value;
    setInputs({ ...inputs, subTasks: updatedSubTasks });
  };

  // Handler for adding more items to dynamic fields
  const handleAddField = (field) => {
    if (field === "reference_links") {
      setInputs((prevInputs) => ({
        ...prevInputs,
        subTasks: [...prevInputs.subTasks, { id: "", name: "", startDate: "", endDate: "", points: 0, assignedUsers: [] }],
      }));
    }
    if (!visibleFields.includes(field)) {
      setVisibleFields((prevFields) => [...prevFields, field]);
      setInputs((prevState) => ({
        ...prevState,
        [field]: [""], // Initialize with the first field
      }));
    }
  };

  const handleAddMore = (field) => {
    setInputs((prevState) => {
      let newEntry;
      if (field === "subTasks") {
        newEntry = { name: "", startDate: "", endDate: "", points: 0, assignedUsers: [] };
      } else if (field === "reference_links") {
        newEntry = ""; // Add a new string for links
      } else if (field === "server_credentials") {
        newEntry = { type: "blockedBy", url: "" }; // Add a new object for server credentials
      } else if (field === "important_documents") {
        newEntry = null; // Placeholder for file
      }
      return {
        ...prevState,
        [field]: [...(prevState[field] || []), newEntry],
      };
    });
  };

  // Remove field dynamically
  const handleRemoveField = (field, index) => {
    setInputs((prevState) => ({
      ...prevState,
      [field]: prevState[field].filter((_, i) => i !== index),
    }));
  };

  // Handler for changing dynamic fields
  const handleChange = useCallback((e, index, fieldType) => {
    const { value } = e.target;
    setInputs((prev) => {
      const updatedField = [...prev[fieldType]];
      updatedField[index] = value;
      return { ...prev, [fieldType]: updatedField };
    });
  }, []);

  // Handler for changing linked issue type
  const handleIssueChange = useCallback((selectedOption, index) => {
    setInputs((prev) => {
      const updatedIssues = [...prev.linkedIssues];
      updatedIssues[index].type = selectedOption;
      return { ...prev, linkedIssues: updatedIssues };
    });
  }, []);

  // Handler for changing linked issue URL
  const handleIssueURLChange = useCallback((e, index) => {
    const { value } = e.target;
    setInputs((prev) => {
      const updatedIssues = [...prev.linkedIssues];
      updatedIssues[index].url = value;
      return { ...prev, linkedIssues: updatedIssues };
    });
  }, []);

  // Handler for changing important documents
  const handleDocumentChange = useCallback((e, index) => {
    const file = e.target.files[0];
    setInputs((prev) => {
      const updatedDocuments = [...prev.importantDocuments];
      updatedDocuments[index] = file;
      return { ...prev, importantDocuments: updatedDocuments };
    });
  }, []);

  // Handler for form submission
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const validationErrors = {};

      // Validate required fields
      if (!inputs.name.trim()) validationErrors.name = "Task name is required.";
      if (inputs.assignedUsers.length === 0) validationErrors.assignedUsers = "At least one user must be assigned.";
      if (inputs.points <= 0) validationErrors.points = "Points must be greater than zero.";
      if (!inputs.startDate) validationErrors.startDate = "Start Date is required.";
      if (!inputs.endDate) validationErrors.endDate = "End Date is required.";
      if (inputs.startDate && inputs.endDate && inputs.startDate > inputs.endDate) validationErrors.endDate = "End Date cannot be before Start Date.";
      if (visibleFields.includes("importantDocuments")) {
        if (inputs.importantDocuments.some((doc) => !doc)) {
          validationErrors.importantDocuments = "All important documents must be uploaded.";
        }
      }

      setErrors(validationErrors);

      if (Object.keys(validationErrors).length === 0) {
        // Implement form submission logic here
        console.log("Form Submitted:", inputs);
        // Reset form or provide success feedback as needed
      } else {
        // Optionally, scroll to the first error or notify the user
        console.log("Validation Errors:", validationErrors);
      }

      const getData = Http.post("/project/type", inputs);
      console.log("getData", getData);
    },
    [inputs, visibleFields]
  );

  const handleSelectChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    setInputs((prev) => ({ ...prev, [name]: selectedOption }));
  };
  return (
    <div>
      {loading ? (
        <ProgressSpinner />
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="container">
            {/* Basic Fields */}
            <div className="row">
              <div className="col-lg-12">
                <label htmlFor="name">
                  Task Name <i className="fa fa-star required"></i>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  id="name"
                  name="name"
                  placeholder="Enter Task Name"
                  value={inputs.name}
                  onChange={handleInputChange}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>
            </div>

            {/* Description Field */}
            <div className="row mt-3">
              <div className="col-lg-12">
                <label htmlFor="description">Description</label>
                <ReactQuill
                  theme="snow"
                  value={inputs.description}
                  onChange={handleQuillChange}
                  placeholder="Enter Task Description"
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: "150px", marginBottom: "40px" }}
                />
                {errors.description && <div className="text-danger">{errors.description}</div>}
              </div>
            </div>

            {/* Date and Time Fields */}
            <div className="row">
              <div className="col-lg-4 mt-2">
                <label htmlFor="taskType">
                  Task Type <i className="fa fa-star required"></i>
                </label>
                <Select
                  id="taskType"
                  name="taskType"
                  options={taskTypeOptions}
                  className={`basic-select ${errors.taskType ? "is-invalid" : ""}`}
                  classNamePrefix="select"
                  value={inputs.taskType}
                  onChange={(selectedOption) => setInputs((prev) => ({ ...prev, taskType: selectedOption }))}
                  placeholder="Select Task Type"
                />
                {errors.taskType && <div className="invalid-feedback d-block">{errors.taskType}</div>}
              </div>
              <div className="col-lg-4 mt-2">
                <label htmlFor="assignedBy">Assigned By</label>
                <Select
                  id="assignedBy"
                  name="assignedBy"
                  options={assignedByOptions}
                  className={`basic-select ${errors.assignedBy ? "is-invalid" : ""}`}
                  classNamePrefix="select"
                  value={inputs.assignedBy}
                  onChange={handleSelectChange}
                  placeholder="Select Assignee"
                  isClearable
                />
                {errors.assignedBy && <div className="invalid-feedback d-block">{errors.assignedBy}</div>}
              </div>
              <div className="col-lg-4 mt-2">
                <label htmlFor="assignedUsers">
                  Assign Users <i className="fa fa-star required"></i>
                </label>
                <Select
                  id="assignedUsers"
                  isMulti
                  name="assignedUsers"
                  options={assignUsersOptions}
                  className={`basic-multi-select ${errors.assignedUsers ? "is-invalid" : ""}`}
                  classNamePrefix="select"
                  value={inputs.assignedUsers}
                  onChange={handleMultiSelectChange}
                  placeholder="Select Users"
                />
                {errors.assignedUsers && <div className="invalid-feedback d-block">{errors.assignedUsers}</div>}
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="points">
                  Points <i className="fa fa-star required"></i>
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.points ? "is-invalid" : ""}`}
                  id="points"
                  name="points"
                  value={inputs.points}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="Enter Points"
                />
                {errors.points && <div className="invalid-feedback">{errors.points}</div>}
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="status">Status</label>
                <Select
                  id="status"
                  name="status"
                  options={statusOptions}
                  classNamePrefix="select"
                  value={inputs.status}
                  onChange={(selectedOption) => setInputs((prev) => ({ ...prev, status: selectedOption }))}
                  placeholder="Select Status"
                  isClearable
                />
                {errors.status && <div className="invalid-feedback d-block">{errors.status}</div>}
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="startDate">
                  Start Date <i className="fa fa-star required"></i>
                </label>
                <input
                  type="date"
                  className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                  id="startDate"
                  name="startDate"
                  value={inputs.startDate}
                  onChange={handleInputChange}
                />
                {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="endDate">
                  End Date <i className="fa fa-star required"></i>
                </label>
                <input
                  type="date"
                  className={`form-control ${errors.endDate ? "is-invalid" : ""}`}
                  id="endDate"
                  name="endDate"
                  value={inputs.endDate}
                  onChange={handleInputChange}
                />
                {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="actualStartDate">Actual Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  id="actualStartDate"
                  name="actualStartDate"
                  value={inputs.actualStartDate}
                  onChange={handleInputChange}
                  disabled
                />
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="actualStartTime">Actual Start Time</label>
                <input
                  type="time"
                  className="form-control"
                  id="actualStartTime"
                  name="actualStartTime"
                  value={inputs.actualStartTime}
                  onChange={handleInputChange}
                  disabled
                />
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="actualEndDate">Actual End Date</label>
                <input
                  type="date"
                  className="form-control"
                  id="actualEndDate"
                  name="actualEndDate"
                  value={inputs.actualEndDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="actualEndTime">Actual End Time</label>
                <input
                  type="time"
                  className="form-control"
                  id="actualEndTime"
                  name="actualEndTime"
                  value={inputs.actualEndTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="estimatedTime">Estimated Time</label>
                <input
                  type="time"
                  className="form-control"
                  id="estimatedTime"
                  name="estimatedTime"
                  value={inputs.estimatedTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-lg-6 mt-2">
                <label htmlFor="documents">File Upload</label>
                <input type="file" className="form-control" id="documents" name="documents" multiple onChange={handleFileChange} />
                <small className="text-muted">Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG</small>
              </div>
            </div>

            {/* Dropdown for additional fields */}
            <div className="row">
              <div className="col-lg">
                <div className="dropdown">
                  <button
                    className="btn btn-secondary dropdown-toggle"
                    type="button"
                    id="dropdownMenuButton"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Add Fields
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    <li>
                      <button className="dropdown-item" type="button" onClick={() => handleAddField("reference_links")}>
                        Sub Task
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" type="button" onClick={() => handleAddField("server_credentials")}>
                        Linked Issues
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Conditionally Render Fields */}
            {visibleFields.includes("subTasks") &&
              inputs.subTasks.map((task, index) => (
                <>
                  <div key={index} className="row mt-4">
                    <div className="">
                      <h6>Sub Task {index + 1}</h6>
                    </div>
                    <div className="">
                      <button type="button" className="btn btn-secondary float-right mt-3" onClick={() => handleAddMore("subTasks")}>
                        Add Sub Task
                      </button>
                    </div>
                    <div className="col-lg-6 mt-2">
                      <label htmlFor={`subTaskName_${index}`}>Task Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id={`subTaskName_${index}`}
                        value={task.name}
                        placeholder="Enter Task Name"
                        onChange={(e) =>
                          setInputs((prevState) => {
                            const updatedSubTasks = [...prevState.subTasks];
                            updatedSubTasks[index].name = e.target.value;
                            return { ...prevState, subTasks: updatedSubTasks };
                          })
                        }
                      />
                    </div>
                    <div className="col-lg-6 mt-2">
                      <label htmlFor={`subTaskUsers_${index}`}>Assign Users</label>
                      <Select
                        id={`subTaskUsers_${index}`}
                        isMulti
                        name={`subTaskUsers_${index}`}
                        options={assignUsersOptions}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        value={task.assignedUsers}
                        onChange={(selected) =>
                          setInputs((prevState) => {
                            const updatedSubTasks = [...prevState.subTasks];
                            updatedSubTasks[index].assignedUsers = selected;
                            return { ...prevState, subTasks: updatedSubTasks };
                          })
                        }
                        placeholder="Select Users"
                      />
                    </div>
                    <div className="col-lg-6 mt-2">
                      <label htmlFor={`subTaskStartDate_${index}`}>Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        id={`subTaskStartDate_${index}`}
                        value={task.startDate}
                        onChange={(e) =>
                          setInputs((prevState) => {
                            const updatedSubTasks = [...prevState.subTasks];
                            updatedSubTasks[index].startDate = e.target.value;
                            return { ...prevState, subTasks: updatedSubTasks };
                          })
                        }
                      />
                    </div>
                    <div className="col-lg-6 mt-2">
                      <label htmlFor={`subTaskEndDate_${index}`}>End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        id={`subTaskEndDate_${index}`}
                        value={task.endDate}
                        onChange={(e) =>
                          setInputs((prevState) => {
                            const updatedSubTasks = [...prevState.subTasks];
                            updatedSubTasks[index].endDate = e.target.value;
                            return { ...prevState, subTasks: updatedSubTasks };
                          })
                        }
                      />
                    </div>
                    <div className="col-lg-6 mt-2">
                      <label htmlFor={`subTaskPoints_${index}`}>Points</label>
                      <input
                        type="number"
                        className="form-control"
                        id={`subTaskPoints_${index}`}
                        value={task.points}
                        onChange={(e) =>
                          setInputs((prevState) => {
                            const updatedSubTasks = [...prevState.subTasks];
                            updatedSubTasks[index].points = e.target.value;
                            return { ...prevState, subTasks: updatedSubTasks };
                          })
                        }
                        min="0"
                        placeholder="Enter Points"
                      />
                    </div>

                    <div className="col-lg-12 mt-2">
                      <button className="btn btn-danger btn-sm" onClick={() => handleRemoveField("subTasks", index)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </>
              ))}
            {visibleFields.includes("server_credentials") && (
              <div className="row mt-4">
                <div className="col-lg-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6>Linked Issues</h6>
                    <button className="btn btn-secondary btn-sm ms-2" onClick={() => handleAddMore("server_credentials")}>
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  {inputs.server_credentials.map((cred, index) => (
                    <div className="my-2" key={`server_${index}`}>
                      {/* URL Field */}
                      <div className="d-flex  align-items-center mb-2">
                        <select className="form-select w-25">
                          <option>Blocked By</option>
                          <option>Blocks</option>
                        </select>
                        <input
                          type="text"
                          className="form-control me-2 g-2"
                          value={cred.url || ""}
                          placeholder={`URL ${index + 1}`}
                          onChange={(e) => {
                            const updatedCred = { ...cred, url: e.target.value };
                            handleChange({ target: { value: updatedCred } }, index, "server_credentials");
                          }}
                        />
                        <button className="btn btn-danger btn-sm" onClick={() => handleRemoveField("server_credentials", index)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {visibleFields.includes("important_documents") && (
              <div className="row mt-4">
                <div className="col-lg-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6>Important Documents</h6>
                    <button className="btn btn-secondary btn-sm ms-2" onClick={() => handleAddMore("important_documents")}>
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                  {inputs.important_documents.map((doc, index) => (
                    <div className="my-2" key={`document_${index}`}>
                      <div className="d-flex align-items-center">
                        <input
                          type="file"
                          className="form-control me-2"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setInputs((prevState) => {
                              const updatedDocuments = [...prevState.important_documents];
                              updatedDocuments[index] = file;
                              return {
                                ...prevState,
                                important_documents: updatedDocuments,
                              };
                            });
                          }}
                        />
                        <button className="btn btn-danger btn-sm" onClick={() => handleRemoveField("important_documents", index)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                      {doc && (
                        <small className="text-muted">
                          Selected file: {doc.name} ({(doc.size / 1024).toFixed(2)} KB)
                        </small>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comment Field */}
            <div className="row mt-4">
              <div className="col-lg-12">
                <label htmlFor="comment">Comment</label>
                <ReactQuill
                  theme="snow"
                  value={inputs.comment}
                  onChange={handleCommentChange}
                  placeholder="Add a comment"
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: "150px", marginBottom: "40px" }}
                />
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Create;
